<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="areas"/>
        <router-link :to="backRoute" class="pi-link pi-back">← Voltar para Áreas</router-link>
        <pi-area-filters :value="areaFilters" :ignored="ignoredLines" @input="setAreaFilters"/>
        <div class="pi-amet__head">
          <pi-export-screen :screen="screen" :filters="exportFilters"/>
        </div>

        <section class="pi-section" aria-labelledby="pi-amet-title">
          <div class="pi-section__head">
            <h2 id="pi-amet-title" class="pi-section__title">{{ titleText }}</h2>
            <pi-definition-tooltip v-if="metric.label" :entry="metric.entry" :label="metric.label"/>
            <p v-if="view.areaLabel" class="pi-section__hint pi-amet__hint">
              <span>{{ view.areaLabel }}<template v-if="view.asOfText"> · Dados de: {{ view.asOfText }}</template></span>
              <pi-definition-tooltip v-if="view.asOfText" info-key="areas.asOf" label="Dados de"/>
            </p>
            <pi-export-menu title="Resumo da métrica" :screen="screen" :columns="metricColumns" :rows="summaryRows" :envelope="cleanDetail"
                            :filters="exportFilters" :disabled="!detailReady"/>
          </div>
          <pi-data-state :loading="detail.loading" :error="detail.error" :envelope="detailState" :empty="detailNotStarted"
                         :empty-message="notStartedText" :show-footer="false" @retry="loadDetail(true)">
            <p v-if="metric.badge || metric.sensitive" class="pi-amet__tags">
              <template v-if="metric.badge">
                <span class="pi-badge" :class="'pi-badge--' + metric.badge.tone" :title="metric.badge.title || null">{{ metric.badge.label }}</span>
                <pi-definition-tooltip :info-key="metric.badge.infoKey" :label="metric.badge.label"/>
                <span v-if="metric.badge.title" class="pi-muted">{{ metric.badge.title }}</span>
              </template>
              <template v-if="metric.sensitive">
                <span class="pi-badge pi-badge--muted">Sensível</span>
                <pi-definition-tooltip info-key="areas.sensitive" label="Métrica sensível"/>
              </template>
            </p>
            <ul class="pi-stats">
              <li v-for="item in stats" :key="item.key" class="pi-stat">
                <span class="pi-stat__head">
                  <span class="pi-stat__label">{{ item.label }}</span>
                  <pi-definition-tooltip :info-key="item.infoKey" :label="item.label"/>
                </span>
                <span class="pi-stat__value">{{ item.text }}</span>
                <span v-if="item.sub" class="pi-stat__sub">{{ item.sub }}</span>
              </li>
            </ul>
            <p v-if="metric.rate && metric.rate.formula" class="pi-muted pi-amet__line">Taxa = {{ metric.rate.formula }}.</p>
            <p v-if="view.coverage.text" class="pi-muted pi-amet__line">
              <span :class="{ 'pi-amet__partial': view.coverage.partial }">{{ view.coverage.text }}</span>
              <pi-definition-tooltip info-key="areas.coverage" label="Cobertura por banco"/>
            </p>
            <div class="pi-amet__ask">
              <router-link :to="view.askRoute" class="pi-btn pi-btn--ghost pi-amet__ask-btn">Pergunte ao especialista</router-link>
              <span class="pi-muted">Abre o Chat com o especialista em {{ view.areaLabel }} e a pergunta pronta; nada é enviado antes de você confirmar.</span>
            </div>
            <nav v-if="siblings.length > 1" class="pi-amet__siblings" :aria-label="'Outras métricas de ' + view.areaLabel">
              <p class="pi-subtitle">Outras métricas de {{ view.areaLabel }}</p>
              <ul class="pi-amet__sibling-list">
                <li v-for="link in siblings" :key="link.key" class="pi-amet__sibling">
                  <span v-if="link.current" class="pi-amet__current" aria-current="page">{{ link.label }}</span>
                  <router-link v-else :to="link.route" class="pi-link">{{ link.label }}</router-link>
                  <pi-definition-tooltip :entry="link.entry" :label="link.label"/>
                  <span class="pi-amet__sibling-value">{{ link.valueText }}</span>
                </li>
              </ul>
            </nav>
          </pi-data-state>
        </section>

        <section v-if="detailReady" class="pi-section" aria-labelledby="pi-amet-series">
          <div class="pi-section__head">
            <h2 id="pi-amet-series" class="pi-section__title">Série diária</h2>
            <pi-definition-tooltip info-key="areas.series" label="Série diária"/>
            <p v-if="view.seriesText" class="pi-section__hint">{{ view.seriesText }}</p>
            <pi-export-menu title="Série diária" :screen="screen" :columns="seriesColumns" :rows="view.series" :envelope="cleanDetail"
                            :filters="exportFilters" :disabled="!view.series.length"/>
          </div>
          <pi-line-chart :series="view.chart" :tooltip-extras="seriesHover" empty-text="Ainda não há retratos para montar a série."/>
        </section>

        <div v-if="detailReady" class="pi-chart-grid">
          <section class="pi-section pi-chart-block" aria-labelledby="pi-amet-size">
            <div class="pi-section__head">
              <h2 id="pi-amet-size" class="pi-section__title">Por porte</h2>
              <pi-export-menu title="Por porte" :screen="screen" :columns="sizeColumns" :rows="view.bySizeBand" :envelope="cleanDetail"
                              :filters="exportFilters" :disabled="!view.bySizeBand.length"/>
            </div>
            <p v-if="censoredText" class="pi-muted pi-amet__line">{{ censoredText }}</p>
            <framed-table>
              <thead>
              <tr>
                <pi-th v-for="column in sizeHeaders" :key="column.key" :column="column"/>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in view.bySizeBand" :key="row.key">
                <td>{{ row.label }}</td>
                <td class="is-num">{{ row.totalText }}</td>
                <td class="is-num">{{ row.companiesText }}</td>
                <template v-if="metric.hasFeature">
                  <td class="is-num">{{ row.eligibleText }}</td>
                  <td class="is-num">{{ row.shareText }}<span v-if="row.shareSub" class="pi-table__sub">{{ row.shareSub }}</span></td>
                </template>
              </tr>
              <tr v-if="!view.bySizeBand.length">
                <td :colspan="sizeHeaders.length" class="pi-muted">Nenhuma empresa no recorte.</td>
              </tr>
              </tbody>
            </framed-table>
          </section>

          <section class="pi-section pi-chart-block" aria-labelledby="pi-amet-segment">
            <div class="pi-section__head">
              <h2 id="pi-amet-segment" class="pi-section__title">Por perfil da empresa</h2>
              <pi-export-menu title="Por perfil da empresa" :screen="screen" :columns="segmentColumns" :rows="view.bySegment" :envelope="cleanDetail"
                              :filters="exportFilters" :disabled="!view.bySegment.length"/>
            </div>
            <p v-if="censoredText" class="pi-muted pi-amet__line">{{ censoredText }}</p>
            <framed-table>
              <thead>
              <tr>
                <pi-th v-for="column in segmentHeaders" :key="column.key" :column="column"/>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in view.bySegment" :key="row.key">
                <td>{{ row.label }}</td>
                <td class="is-num">{{ row.totalText }}</td>
                <td class="is-num">{{ row.companiesText }}</td>
                <template v-if="metric.hasFeature">
                  <td class="is-num">{{ row.eligibleText }}</td>
                  <td class="is-num">{{ row.shareText }}<span v-if="row.shareSub" class="pi-table__sub">{{ row.shareSub }}</span></td>
                </template>
              </tr>
              <tr v-if="!view.bySegment.length">
                <td :colspan="segmentHeaders.length" class="pi-muted">Nenhuma empresa no recorte.</td>
              </tr>
              </tbody>
            </framed-table>
          </section>
        </div>

        <section v-if="detailReady" class="pi-section" aria-labelledby="pi-amet-companies">
          <div class="pi-section__head">
            <h2 id="pi-amet-companies" class="pi-section__title">Empresas</h2>
            <p v-if="companies.loaded" class="pi-section__hint">{{ companiesCountText }}</p>
            <pi-export-menu title="Empresas da métrica" :screen="screen" :columns="companyColumns" :load="exportCompanies"
                            :filters="companiesExportFilters" :disabled="!companies.loaded || !!companies.error || !companyRows.length"/>
          </div>
          <p class="pi-muted pi-amet__line">{{ companiesLeadText }}</p>
          <pi-data-state :loading="companies.loading" :error="companies.error" :envelope="companiesState" :empty="companiesEmpty"
                         empty-message="Nenhuma empresa com valor no recorte." :show-footer="false" @retry="loadCompanies(true)">
            <framed-table>
              <thead>
              <tr>
                <template v-for="column in companyHeaders">
                  <pi-sort-th v-if="column.sortable" :key="column.key" :column="column" :state="sortState" :disabled="companies.loading" @sort="sortBy"/>
                  <pi-th v-else :key="column.key" :column="column"/>
                </template>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in companyRows" :key="row.key">
                <td class="is-num">{{ row.rankText }}</td>
                <td><pi-company-name :company-id="row.companyId" :shard-code="row.shardCode" :name="row.name" stacked/></td>
                <td>
                  {{ row.profileText }}
                  <span v-if="row.journeyText" class="pi-table__sub">Jornada {{ row.journeyText }}</span>
                  <span v-if="row.trial" class="pi-badge pi-badge--info pi-amet__trial">Trial</span>
                </td>
                <td v-if="metric.hasFeature">{{ row.featureText }}</td>
                <td class="is-num">{{ row.valueText }}</td>
                <template v-if="hasParts">
                  <td class="is-num">{{ row.numeratorText }}</td>
                  <td class="is-num">{{ row.denominatorText }}</td>
                  <td class="is-num">{{ row.shareText }}</td>
                </template>
              </tr>
              </tbody>
              <table-pager slot="footer" :total="companiesTotal" :page="page" :page-size="pageSize"
                           @update:page="changePage" @update:pageSize="changePageSize"/>
            </framed-table>
          </pi-data-state>
        </section>

        <pi-page-footer :envelopes="[cleanDetail, cleanCompanies]" :labels="['Métrica', 'Empresas']"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import framedTable from "../../components/default/framedTable.vue";
import tablePager from "../../components/default/tablePager.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import piExportScreen from "../../components/pi/piExportScreen.vue";
import piLineChart from "../../components/pi/piLineChart.vue";
import piSortTh from "../../components/pi/piSortTh.vue";
import piTh from "../../components/pi/piTh.vue";
import piCompanyName from "../../components/pi/piCompanyName.vue";
import piAreaFilters from "../../components/pi/piAreaFilters.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { fetchAllRows, piGet } from "../../components/pi/piApi.js";
import { screenColumns } from "../../components/pi/piColumns.js";
import { exportProvide, filterLines } from "../../components/pi/piExport.js";
import { formatInteger } from "../../components/pi/piFormat.js";
import { PI_FILTER_DEFAULTS, PI_FILTER_KEY, ignoredFilters, toQueryString } from "../../components/pi/piQuery.js";
import { blankRequest, failRequest, listOf, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import { pageOf, totalOf } from "../../components/pi/piTable.js";
import {
  AREAS_NOT_STARTED, AREA_COMPANY_COLUMNS, AREA_COMPANY_PAGE_SIZE, AREA_FILTER_FIELDS, AREA_METRIC_COLUMNS, AREA_SCREEN, AREA_SEGMENT_CUT_COLUMNS,
  AREA_SERIES_COLUMNS, AREA_SIZE_CUT_COLUMNS, COMPANIES_NOTE_TEXTS, DEFAULT_AREA_COMPANY_SORT, NOT_STARTED_NOTE_PREFIX, SHARED_NOTE_PREFIXES,
  areaCompanyRow, areaEnvelope, areaFilterLines, areaFiltersOf, areaParams, areaQueryState, areasRoute, companiesLead, companyColumnsFor,
  cutColumnsFor, metricDetailView, metricLabels, metricPath, metricStats, nextAreaSort, parseMetricKey, seriesExtras, sharedFilterPatch, siblingLinks,
  sortLine, validAreaSort,
} from "../../components/pi/piAreasModel.js";
import { PI_AREAS, PI_AREA_METRIC, PI_AREA_METRIC_COMPANIES } from "../../../../urlConfig";

const INVALID_PARAM = {
  kind: "invalid",
  status: null,
  message: "O endereço não traz uma métrica de área válida.",
  retryable: false,
};

export default {
  name: "pi-area-metric",
  components: {
    boxDefault, framedTable, tablePager, piTabs, piDataState, piPageFooter, piDefinitionTooltip, piExportMenu, piExportScreen, piLineChart,
    piSortTh, piTh, piCompanyName, piAreaFilters,
  },
  mixins: [listFilterState(PI_FILTER_KEY, PI_FILTER_DEFAULTS)],
  provide: exportProvide,
  data() {
    return {
      detail: blankRequest(),
      companies: blankRequest(),
      area: blankRequest(),
      journey: areaQueryState(this.$route && this.$route.query).journey,
      sort: Object.assign({}, DEFAULT_AREA_COMPANY_SORT),
      page: 1,
      pageSize: AREA_COMPANY_PAGE_SIZE,
      metricColumns: AREA_METRIC_COLUMNS,
      seriesColumns: AREA_SERIES_COLUMNS,
      notStartedText: AREAS_NOT_STARTED,
      screen: AREA_SCREEN,
    };
  },
  computed: {
    metricKey() {
      return parseMetricKey(this.$route.params.key);
    },
    areaFilters() {
      return areaFiltersOf(this.filters, this.journey);
    },
    ignoredLines() {
      return ignoredFilters(this.filters, AREA_FILTER_FIELDS);
    },
    baseParams() {
      return areaParams(this.areaFilters);
    },
    baseKey() {
      return this.metricKey + "?" + toQueryString(this.baseParams);
    },
    labels() {
      return metricLabels(payloadOf(this.area.envelope).areas);
    },
    view() {
      return metricDetailView(payloadOf(this.detail.envelope), this.labels, { filters: this.areaFilters });
    },
    metric() {
      return this.view.metric;
    },
    detailNotStarted() {
      return this.detail.loaded && !this.detail.error && this.view.notStarted;
    },
    detailReady() {
      return !!this.metricKey && this.detail.loaded && !this.detail.error && !this.view.notStarted;
    },
    titleText() {
      return this.metric.label || "Métrica da área";
    },
    cleanDetail() {
      return areaEnvelope(this.detail.envelope);
    },
    detailState() {
      return this.detailNotStarted ? areaEnvelope(this.detail.envelope, SHARED_NOTE_PREFIXES.concat([NOT_STARTED_NOTE_PREFIX])) : this.cleanDetail;
    },
    stats() {
      return metricStats(this.metric, this.view);
    },
    summaryRows() {
      return this.detailReady ? [this.metric] : [];
    },
    seriesHover() {
      return seriesExtras(this.view.series);
    },
    sizeColumns() {
      return cutColumnsFor(AREA_SIZE_CUT_COLUMNS, this.metric.hasFeature);
    },
    segmentColumns() {
      return cutColumnsFor(AREA_SEGMENT_CUT_COLUMNS, this.metric.hasFeature);
    },
    sizeHeaders() {
      return screenColumns(this.sizeColumns);
    },
    segmentHeaders() {
      return screenColumns(this.segmentColumns);
    },
    censoredText() {
      const count = this.view.censored;
      if (!count) return "";
      return formatInteger(count) + (count === 1 ? " empresa com menos de 5 na base ficou" : " empresas com menos de 5 na base ficaram")
        + " fora de todos os números (métrica sensível).";
    },
    companiesPayload() {
      return payloadOf(this.companies.envelope);
    },
    hasParts() {
      return this.companies.loaded ? this.companiesPayload.hasParts === true : !!this.metric.rate;
    },
    sortState() {
      return validAreaSort(this.sort, this.hasParts);
    },
    companiesParams() {
      return areaParams(this.areaFilters, { sort: this.sortState.sort, desc: this.sortState.desc, page: this.page, pageSize: this.pageSize });
    },
    companiesKey() {
      return this.metricKey ? this.metricKey + "?" + toQueryString(this.companiesParams) : "";
    },
    companyColumns() {
      return companyColumnsFor(AREA_COMPANY_COLUMNS, this.metric.hasFeature, this.hasParts);
    },
    companyHeaders() {
      return screenColumns(this.companyColumns);
    },
    companyRows() {
      return listOf(this.companiesPayload.rows).map(areaCompanyRow);
    },
    companiesTotal() {
      return totalOf(this.companiesPayload, this.companyRows.length);
    },
    companiesEmpty() {
      return this.companies.loaded && !this.companyRows.length;
    },
    companiesCountText() {
      const total = this.companiesTotal;
      return formatInteger(total) + (total === 1 ? " empresa na lista" : " empresas na lista");
    },
    companiesLeadText() {
      return companiesLead(Object.assign({ hasParts: this.hasParts }, this.companiesPayload), this.metric.rate ? this.metric.rate.formula : "");
    },
    companiesState() {
      return areaEnvelope(this.companies.envelope, SHARED_NOTE_PREFIXES.concat(COMPANIES_NOTE_TEXTS));
    },
    cleanCompanies() {
      return areaEnvelope(this.companies.envelope);
    },
    areaKey() {
      return this.detailReady ? this.view.areaKey : "";
    },
    areaRequestKey() {
      return this.areaKey ? toQueryString(areaParams(this.areaFilters, { area: this.areaKey })) : "";
    },
    siblings() {
      return siblingLinks(payloadOf(this.area.envelope), this.metricKey, this.$route.query, this.journey);
    },
    backRoute() {
      return areasRoute(this.$route.query, this.journey, this.view.areaKey);
    },
    exportFilters() {
      const extra = areaFilterLines(this.areaFilters);
      if (this.view.areaLabel) extra.push("Área: " + this.view.areaLabel);
      if (this.metric.label) extra.push("Métrica: " + this.metric.label + " (" + this.metric.key + ")");
      extra.push(this.view.snapshotLine);
      return filterLines({ filters: this.filters, fields: AREA_FILTER_FIELDS, extra: extra });
    },
    companiesExportFilters() {
      return this.exportFilters.concat([sortLine(this.companyColumns, this.sortState)]);
    },
  },
  watch: {
    baseKey() {
      this.page = 1;
      this.loadDetail(false);
    },
    metricKey() {
      this.sort = Object.assign({}, DEFAULT_AREA_COMPANY_SORT);
    },
    companiesKey() {
      this.loadCompanies(false);
    },
    areaRequestKey() {
      this.loadArea(false);
    },
    "$route.query"(query) {
      const seeded = areaQueryState(query);
      if (seeded.present) this.journey = seeded.journey;
    },
    "$route.params.key"() {
      this.keepCanonicalPath();
    },
  },
  created() {
    this.keepCanonicalPath();
    this.loadDetail(false);
    this.loadCompanies(false);
  },
  methods: {
    keepCanonicalPath() {
      const path = metricPath(this.$route.params.key);
      if (path && path !== this.$route.path) this.$router.replace({ path: path, query: this.$route.query }, () => {}, () => {});
    },
    loadDetail(force) {
      const key = this.baseKey;
      if (!shouldLoad(this.detail, key, force)) return;
      if (!this.metricKey) {
        failRequest(this.detail, INVALID_PARAM, key);
        return;
      }
      const metricKey = this.metricKey;
      runRequest(this.detail, () => piGet(PI_AREA_METRIC(metricKey), this.baseParams), "carregar a métrica da área", key);
    },
    loadCompanies(force) {
      const key = this.companiesKey;
      if (!key || !shouldLoad(this.companies, key, force)) return;
      const metricKey = this.metricKey;
      runRequest(this.companies, () => piGet(PI_AREA_METRIC_COMPANIES(metricKey), this.companiesParams), "carregar as empresas da métrica", key);
    },
    loadArea(force) {
      const key = this.areaRequestKey;
      if (!key || !shouldLoad(this.area, key, force)) return;
      const params = areaParams(this.areaFilters, { area: this.areaKey });
      runRequest(this.area, () => piGet(PI_AREAS, params), "carregar as outras métricas da área", key);
    },
    setAreaFilters(next) {
      const patch = sharedFilterPatch(this.filters, next);
      if (Object.keys(patch).length) this.filters = Object.assign({}, this.filters, patch);
      this.journey = next && next.journey ? next.journey : "";
    },
    sortBy(column) {
      this.sort = nextAreaSort(this.sortState, column);
      this.page = 1;
    },
    changePage(page) {
      this.page = pageOf({ page: page }, 1);
    },
    changePageSize(size) {
      this.pageSize = size;
      this.page = 1;
    },
    exportCompanies() {
      return fetchAllRows(PI_AREA_METRIC_COMPANIES(this.metricKey), this.companiesParams)
        .then((page) => Object.assign({}, page, { envelope: areaEnvelope(page.envelope) }));
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-amet__head {
  display: flex;
  justify-content: flex-end;
  margin: 0 0 8px;
}

.pi-amet__hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pi-amet__tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px;
}

.pi-amet__line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-bottom: 10px;
}

.pi-amet__partial {
  color: #7a4f00;
  font-weight: 600;
}

.pi-amet__ask {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin: 4px 0 16px;
}

.pi-amet__ask-btn {
  text-decoration: none;
}

.pi-amet__siblings {
  margin-top: 8px;
}

.pi-amet__sibling-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.pi-amet__sibling {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
}

.pi-amet__current {
  font-weight: 700;
  color: #1f2933;
}

.pi-amet__sibling-value {
  font-size: 11px;
  color: #6b7280;
}

.pi-amet__trial {
  margin-top: 2px;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piAreaMetric.vue